import React, { useState } from 'react';
import axios from 'axios';
import { Check, Copy, MapPin, Store, User } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import { API_TIMEOUT, apiBaseUrl } from '../config/api_config';
import { useLanguage } from '../i18n/LanguageContext';

function StoreBadges({ playUrl, appUrl, t }) {
  return (
    <div className="store-badges">
      <a href={playUrl} target="_blank" rel="noopener noreferrer" className="store-badge" aria-label={t.demo.googlePlay}>
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <path fill="#EA4335" d="M3.6 2.2 13.3 12 3.6 21.8c-.4-.2-.6-.6-.6-1V3.2c0-.4.2-.8.6-1z" />
          <path fill="#FBBC04" d="m13.3 12 2.8-2.8 4.5 2.6c.6.3.6 1.1 0 1.4l-4.5 2.6L13.3 12z" />
          <path fill="#4285F4" d="M13.3 12 3.6 2.2c.3-.2.6-.2.9 0L16.1 9.2 13.3 12z" />
          <path fill="#34A853" d="M13.3 12 16.1 14.8 4.5 21.8c-.3.2-.6.2-.9 0L13.3 12z" />
        </svg>
        <span>{t.demo.googlePlay}</span>
      </a>
      <a href={appUrl} target="_blank" rel="noopener noreferrer" className="store-badge" aria-label={t.demo.appStore}>
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <path
            fill="currentColor"
            d="M16.4 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.7zM14.3 6.4c.6-.7 1-1.7.9-2.7-1 .1-2.1.6-2.7 1.4-.6.6-1.1 1.7-.9 2.6 1 .1 2-.5 2.7-1.3z"
          />
        </svg>
        <span>{t.demo.appStore}</span>
      </a>
    </div>
  );
}

export default function DemoPage() {
  const { t, isAr } = useLanguage();

  const [storeName, setStoreName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [versionType, setVersionType] = useState('offline');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result?.activation_key) return;
    try {
      await navigator.clipboard.writeText(result.activation_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t.demo.copyFailed);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const digits = (whatsapp || '').replace(/\D/g, '');
    if (
      !storeName.trim() ||
      !ownerName.trim() ||
      !address.trim() ||
      !versionType ||
      digits.length < 10
    ) {
      setError(t.demo.validation);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${apiBaseUrl}/haseela/public/trial-request/`,
        {
          store_name: storeName.trim(),
          owner_name: ownerName.trim(),
          phone_num: whatsapp.trim(),
          address: address.trim(),
          version_type: versionType,
        },
        { timeout: API_TIMEOUT, headers: { 'Content-Type': 'application/json' } }
      );
      setResult(data);
    } catch (err) {
      const payload = err.response?.data;
      const msg = isAr
        ? payload?.error || t.demo.submitError
        : payload?.error_en || payload?.error || t.demo.submitError;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (result?.success) {
    const isOffline = result.version_type === 'offline';
    return (
      <section className="section page-top">
        <div className="container narrow">
          <div className="section-head">
            <h1>{t.demo.successTitle}</h1>
            <p>{t.demo.successSubtitle}</p>
          </div>

          <div className="demo-success">
            {isOffline && result.activation_key ? (
              <div className="activation-box">
                <h2>{t.demo.activationCode}</h2>
                <div className="activation-row">
                  <code>{result.activation_key}</code>
                  <button type="button" className="btn btn-primary" onClick={handleCopy}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? t.demo.copied : t.demo.copy}
                  </button>
                </div>
                <p className="form-note">{t.demo.activationHint}</p>
              </div>
            ) : null}

            <div className="download-box">
              <h2>{t.demo.downloadApps}</h2>
              <StoreBadges
                playUrl={result.play_store_url}
                appUrl={result.app_store_url}
                t={t}
              />
            </div>

            <button
              type="button"
              className="btn btn-soft full"
              onClick={() => {
                setResult(null);
                setStoreName('');
                setOwnerName('');
                setWhatsapp('');
                setAddress('');
                setVersionType('offline');
              }}
            >
              {t.demo.newRequest}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section page-top">
      <div className="container narrow">
        <div className="section-head">
          <h1>{t.demo.title}</h1>
          <p>{t.demo.subtitle}</p>
        </div>

        <form className="demo-form" onSubmit={handleSubmit}>
          <label>
            <span>{t.demo.storeName}</span>
            <div className="input-wrap">
              <Store size={18} />
              <input
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder={t.demo.storePlaceholder}
                required
              />
            </div>
          </label>

          <label>
            <span>{t.demo.ownerName}</span>
            <div className="input-wrap">
              <User size={18} />
              <input
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder={t.demo.ownerPlaceholder}
                required
              />
            </div>
          </label>

          <label>
            <span>{t.demo.whatsapp}</span>
            <PhoneInput
              value={whatsapp}
              onChange={setWhatsapp}
              placeholder={t.demo.whatsappPlaceholder}
              required
              digitsLabel={t.demo.digits}
            />
          </label>

          <label>
            <span>{t.demo.versionType}</span>
            <select
              className="native-select"
              value={versionType}
              onChange={(e) => setVersionType(e.target.value)}
              required
            >
              <option value="offline">{t.demo.offline}</option>
              <option value="online">{t.demo.online}</option>
            </select>
          </label>

          <label>
            <span>{t.demo.address}</span>
            <div className="input-wrap">
              <MapPin size={18} />
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t.demo.addressPlaceholder}
                required
              />
            </div>
          </label>

          {error ? <p className="form-error">{error}</p> : null}

          <button type="submit" className="btn btn-primary btn-lg full" disabled={loading}>
            {loading ? t.demo.submitting : t.demo.submit}
          </button>
          <p className="form-note">{t.demo.note}</p>
        </form>
      </div>
    </section>
  );
}
